import React, { useState, useEffect } from 'react';
import { X, MapPin, Building, Users, Activity, Phone, Mail } from 'lucide-react';
import { getAllProvinces, getTerritoriesByProvince, getSectorsByTerritory, getChiefTownsBySector } from '../../data/rdcGeography';

const HealthZoneAddModal = ({ isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    province: '',
    territory: '',
    sector: '',
    chiefTown: '',
    population: '',
    area: '',
    coordinates: { latitude: '', longitude: '' },
    healthCenters: '',
    hospitals: '',
    healthPosts: '',
    doctors: '',
    nurses: '',
    midwives: '',
    healthWorkers: '',
    budget: '',
    infrastructure: [],
    services: [],
    challenges: '',
    priorities: '',
    contactPerson: '',
    contactPhone: '',
    contactEmail: '',
    status: 'Fonctionnelle',
    isActive: true,
    lastEvaluation: '',
    nextEvaluation: '',
    notes: ''
  });

  const [errors, setErrors] = useState({});
  const [availableTerritories, setAvailableTerritories] = useState([]);
  const [availableSectors, setAvailableSectors] = useState([]);
  const [availableChiefTowns, setAvailableChiefTowns] = useState([]);

  const provinces = getAllProvinces();

  const statuses = [
    'Fonctionnelle',
    'Partiellement fonctionnelle', 
    'Non fonctionnelle',
    'En construction'
  ];

  const infrastructureOptions = [
    'Électricité', 'Eau potable', 'Internet', 'Téléphone',
    'Ambulance', 'Laboratoire', 'Radiologie', 'Bloc opératoire',
    'Maternité', 'Pharmacie', 'Morgue', 'Cuisine'
  ];

  const servicesOptions = [
    'Consultation générale', 'Maternité', 'Pédiatrie', 'Chirurgie',
    'Laboratoire', 'Radiologie', 'Pharmacie', 'Vaccination',
    'Planning familial', 'Soins d\'urgence', 'Rééducation',
    'Médecine traditionnelle', 'Santé mentale', 'Dentisterie'
  ];

  // Effet pour charger les territoires quand la province change
  useEffect(() => {
    if (formData.province) {
      const territories = getTerritoriesByProvince(formData.province);
      setAvailableTerritories(territories);
      // Reset les champs dépendants
      setFormData(prev => ({
        ...prev,
        territory: '',
        sector: '',
        chiefTown: ''
      }));
      setAvailableSectors([]);
      setAvailableChiefTowns([]);
    } else {
      setAvailableTerritories([]);
      setAvailableSectors([]);
      setAvailableChiefTowns([]);
    }
  }, [formData.province]);

  // Effet pour charger les secteurs quand le territoire change
  useEffect(() => {
    if (formData.province && formData.territory) {
      const sectors = getSectorsByTerritory(formData.province, formData.territory);
      setAvailableSectors(sectors);
      // Reset les champs dépendants
      setFormData(prev => ({
        ...prev,
        sector: '',
        chiefTown: ''
      }));
      setAvailableChiefTowns([]);
    } else {
      setAvailableSectors([]);
      setAvailableChiefTowns([]);
    }
  }, [formData.province, formData.territory]);

  // Effet pour charger les chefs-lieux quand le secteur change
  useEffect(() => {
    if (formData.province && formData.territory && formData.sector) {
      const chiefTowns = getChiefTownsBySector(formData.province, formData.territory, formData.sector);
      setAvailableChiefTowns(chiefTowns);
      // Reset le champ dépendant
      setFormData(prev => ({
        ...prev,
        chiefTown: ''
      }));
    } else {
      setAvailableChiefTowns([]);
    }
  }, [formData.province, formData.territory, formData.sector]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'latitude' || name === 'longitude') {
      setFormData(prev => ({
        ...prev,
        coordinates: {
          ...prev.coordinates,
          [name]: value
        }
      }));
    } else if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleArrayChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Le nom est requis';
    if (!formData.code.trim()) newErrors.code = 'Le code est requis';
    if (!formData.province) newErrors.province = 'La province est requise';
    if (!formData.territory.trim()) newErrors.territory = 'Le territoire est requis';
    if (!formData.chiefTown.trim()) newErrors.chiefTown = 'Le chef-lieu est requis';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    // Convertir les chaînes vides en null pour les champs numériques
    const processedData = {
      ...formData,
      population: formData.population ? parseInt(formData.population) : null,
      area: formData.area ? parseFloat(formData.area) : null,
      healthCenters: formData.healthCenters ? parseInt(formData.healthCenters) : 0,
      hospitals: formData.hospitals ? parseInt(formData.hospitals) : 0,
      healthPosts: formData.healthPosts ? parseInt(formData.healthPosts) : 0,
      doctors: formData.doctors ? parseInt(formData.doctors) : 0,
      nurses: formData.nurses ? parseInt(formData.nurses) : 0,
      midwives: formData.midwives ? parseInt(formData.midwives) : 0,
      healthWorkers: formData.healthWorkers ? parseInt(formData.healthWorkers) : 0,
      budget: formData.budget ? parseFloat(formData.budget) : null,
      coordinates: formData.coordinates.latitude && formData.coordinates.longitude 
        ? {
            latitude: parseFloat(formData.coordinates.latitude),
            longitude: parseFloat(formData.coordinates.longitude)
          }
        : null,
      lastEvaluation: formData.lastEvaluation || null,
      nextEvaluation: formData.nextEvaluation || null
    };

    onSave(processedData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Ajouter une Zone de Santé</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Informations de base */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <MapPin className="h-5 w-5 mr-2" />
              Informations de base
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom de la zone *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                    errors.name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Ex: Zone de Santé de Kinshasa"
                />
                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Code de la zone *
                </label>
                <input
                  type="text"
                  name="code"
                  value={formData.code}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                    errors.code ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Ex: ZS001"
                />
                {errors.code && <p className="text-red-500 text-sm mt-1">{errors.code}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Province *
                </label>
                <select
                  name="province"
                  value={formData.province}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                    errors.province ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Sélectionner une province</option>
                  {provinces.map(province => (
                    <option key={province} value={province}>{province}</option>
                  ))}
                </select>
                {errors.province && <p className="text-red-500 text-sm mt-1">{errors.province}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Territoire *
                </label>
                <select
                  name="territory"
                  value={formData.territory}
                  onChange={handleChange}
                  disabled={!formData.province}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                    errors.territory ? 'border-red-500' : 'border-gray-300'
                  } ${!formData.province ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                >
                  <option value="">
                    {!formData.province ? 'Sélectionnez d\'abord une province' : 'Sélectionner un territoire'}
                  </option>
                  {availableTerritories.map(territory => (
                    <option key={territory} value={territory}>{territory}</option>
                  ))}
                </select>
                {errors.territory && <p className="text-red-500 text-sm mt-1">{errors.territory}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Secteur
                </label>
                <select
                  name="sector"
                  value={formData.sector}
                  onChange={handleChange}
                  disabled={!formData.territory}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                    !formData.territory ? 'bg-gray-100 cursor-not-allowed' : ''
                  }`}
                >
                  <option value="">
                    {!formData.territory ? 'Sélectionnez d\'abord un territoire' : 'Sélectionner un secteur'}
                  </option>
                  {availableSectors.map(sector => (
                    <option key={sector} value={sector}>{sector}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Chef-lieu *
                </label>
                <select
                  name="chiefTown"
                  value={formData.chiefTown}
                  onChange={handleChange}
                  disabled={!formData.sector}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                    errors.chiefTown ? 'border-red-500' : 'border-gray-300'
                  } ${!formData.sector ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                >
                  <option value="">
                    {!formData.sector ? 'Sélectionnez d\'abord un secteur' : 'Sélectionner un chef-lieu'}
                  </option>
                  {availableChiefTowns.map(chiefTown => (
                    <option key={chiefTown} value={chiefTown}>{chiefTown}</option>
                  ))}
                </select>
                {errors.chiefTown && <p className="text-red-500 text-sm mt-1">{errors.chiefTown}</p>}
              </div>
            </div>
          </div>

          {/* Démographie et géographie */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <Users className="h-5 w-5 mr-2" />
              Démographie et géographie
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Population
                </label>
                <input
                  type="number"
                  name="population"
                  value={formData.population}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Ex: 1000000"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Superficie (km²)
                </label>
                <input
                  type="number"
                  step="0.01"
                  name="area"
                  value={formData.area}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Ex: 9965.21"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Statut
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  {statuses.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Latitude
                </label>
                <input
                  type="number"
                  step="any"
                  name="latitude"
                  value={formData.coordinates.latitude}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Ex: -4.4419"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Longitude
                </label>
                <input
                  type="number"
                  step="any"
                  name="longitude"
                  value={formData.coordinates.longitude}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Ex: 15.2663"
                />
              </div>
            </div>
          </div>

          {/* Infrastructures de santé */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <Building className="h-5 w-5 mr-2" />
              Infrastructures de santé
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Centres de santé
                </label>
                <input
                  type="number"
                  name="healthCenters"
                  value={formData.healthCenters}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hôpitaux
                </label>
                <input
                  type="number"
                  name="hospitals"
                  value={formData.hospitals}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Postes de santé
                </label>
                <input
                  type="number"
                  name="healthPosts"
                  value={formData.healthPosts}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Budget annuel (USD)
                </label>
                <input
                  type="number"
                  step="0.01"
                  name="budget"
                  value={formData.budget}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>

          {/* Personnel de santé */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <Activity className="h-5 w-5 mr-2" />
              Personnel de santé
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Médecins
                </label>
                <input
                  type="number"
                  name="doctors"
                  value={formData.doctors}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Infirmiers
                </label>
                <input
                  type="number"
                  name="nurses"
                  value={formData.nurses}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sages-femmes
                </label>
                <input
                  type="number"
                  name="midwives"
                  value={formData.midwives}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Agents de santé
                </label>
                <input
                  type="number"
                  name="healthWorkers"
                  value={formData.healthWorkers}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="0"
                />
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <Phone className="h-5 w-5 mr-2" />
              Contact
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Personne de contact
                </label>
                <input
                  type="text"
                  name="contactPerson"
                  value={formData.contactPerson}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Ex: Dr. Jean Mukamba"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Téléphone
                </label>
                <input
                  type="tel"
                  name="contactPhone"
                  value={formData.contactPhone}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Ex: +243 81 234 5678"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  name="contactEmail"
                  value={formData.contactEmail}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Ex: contact@zonesante.cd"
                />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes et observations
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Notes additionnelles..."
              />
            </div>
          </div>

          {/* Boutons */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors duration-200"
            >
              Ajouter la zone
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default HealthZoneAddModal;
